import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Content } from "../../components/wrappers/Content";
import { useUserStore, workspaceRole } from "../../stores/userStore";
import { useFetch } from "../../lib/useFetch";
import OpsHome from "./OpsHome";
import FinanceHome from "./FinanceHome";
import AdminHome from "./AdminHome";

/** Role-aware home (Phase C): brokers get their pipeline home; ops the
 *  departures/funding view; finance the money-in/out view; admin + viewer the
 *  commercial summary. */

export default function Home() {
  const user = useUserStore().user;
  switch (workspaceRole(user)) {
    case "ops":
      return <OpsHome />;
    case "finance":
      return <FinanceHome />;
    case "admin":
    case "viewer":
      return <AdminHome />;
    default:
      return <BrokerHome />;
  }
}

/** Broker home (Phase 1, A1) — wired to /offers/workspace/home + the queue's overdue cards. */

type HomeData = {
  kpis: { openEnquiries: number; liveOffers: number; confirmedThisMonth: number; winRate90: number | null };
  activity: {
    id: string;
    type: string;
    metadata: Record<string, unknown> | null;
    createdAt: string;
    offerId?: string;
    offerTitle?: string;
    clientName: string | null;
  }[];
  departures: { id: string; from: string; to: string; clientName: string | null; yachtName: string | null }[];
};

type QueueCard = {
  id: string;
  clientName: string | null;
  leadYacht: string | null;
  nextAction: string;
  daysInStage: number;
  overdue: boolean;
  brokerId: string | null;
};

const ACTIVITY_LABEL: Record<string, string> = {
  sent: "offer sent",
  opened: "opened the offer",
  reacted: "reacted",
  feedback: "sent feedback",
  accepted: "accepted a yacht",
  converted: "converted to booking",
};

function BrokerHome() {
  const user = useUserStore().user;
  const { fetch } = useFetch();
  const [data, setData] = useState<HomeData | null>(null);
  const [attention, setAttention] = useState<QueueCard[]>([]);

  useEffect(() => {
    fetch<{ data: HomeData }>("/api/offers/workspace/home")
      .then((r) => setData(r.data))
      .catch(() => null);
    fetch<{ data: QueueCard[] }>("/api/offers/workspace/queue")
      .then((r) =>
        setAttention(
          (r.data ?? [])
            // brokerId on a card is the DB user id — compare with databaseId, not the HubSpot owner id.
            .filter((c) => c.overdue && (user?.databaseId == null || c.brokerId === String(user.databaseId)))
            .sort((a, b) => b.daysInStage - a.daysInStage)
            .slice(0, 6),
        ),
      )
      .catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

  const kpis = [
    { label: "Open enquiries", value: data?.kpis.openEnquiries },
    { label: "Live offers", value: data?.kpis.liveOffers },
    { label: "Confirmed this month", value: data?.kpis.confirmedThisMonth },
    { label: "Win rate (90d)", value: data?.kpis.winRate90 != null ? `${data.kpis.winRate90}%` : "—" },
  ];

  return (
    <Content title={`${greeting}${user?.firstName ? `, ${user.firstName}` : ""}`}>
      <div className="flex flex-col gap-6 p-1">
        <p className="text-sm text-slate-500 -mt-2">{today}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="rounded-xl border bg-white p-4">
              <div className="text-sm text-slate-500">{kpi.label}</div>
              <div className="text-2xl font-semibold mt-1">{kpi.value ?? "…"}</div>
            </div>
          ))}
        </div>
        <div className="grid lg:grid-cols-[2fr_1fr] gap-4 items-start">
          <div className="flex flex-col gap-4">
            <div className="rounded-xl border bg-white p-4">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="font-semibold">Needs your attention</h3>
                <Link className="text-sm text-blue-600 hover:underline" to="/queue">
                  Open queue
                </Link>
              </div>
              {attention.length === 0 ? (
                <p className="text-sm text-slate-500">Nothing overdue — clear runway.</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {attention.map((c) => (
                      <tr key={c.id} className="border-b last:border-b-0">
                        <td className="py-2 pr-3 font-medium">
                          <Link className="hover:underline" to={`/offers/${c.id}`}>
                            {c.clientName ?? "—"}
                          </Link>
                        </td>
                        <td className="py-2 pr-3 text-slate-500">{c.leadYacht ?? ""}</td>
                        <td className="py-2 pr-3 text-red-600 font-medium">{c.nextAction}</td>
                        <td className="py-2 text-right text-slate-400 whitespace-nowrap">{c.daysInStage}d</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="rounded-xl border bg-white p-4">
              <h3 className="font-semibold mb-2">Your upcoming departures</h3>
              {!data?.departures?.length ? (
                <p className="text-sm text-slate-500">No departures in the next 45 days.</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {data.departures.map((d) => (
                      <tr key={d.id} className="border-b last:border-b-0">
                        <td className="py-2 pr-3 whitespace-nowrap font-medium">
                          {new Date(d.from).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                        </td>
                        <td className="py-2 pr-3">{d.clientName ?? "—"}</td>
                        <td className="py-2 text-slate-500">{d.yachtName ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div className="rounded-xl border bg-white p-4">
              <h3 className="font-semibold mb-2">Quick actions</h3>
              <div className="flex flex-col gap-2 text-sm">
                <Link className="text-blue-600 hover:underline" to="/offers/add">New offer</Link>
                <Link className="text-blue-600 hover:underline" to="/fleet">Search the fleet</Link>
                <Link className="text-blue-600 hover:underline" to="/queue">Open my queue</Link>
              </div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <h3 className="font-semibold mb-2">Recent client activity</h3>
              {!data?.activity?.length ? (
                <p className="text-sm text-slate-500">Client opens, reactions and feedback appear here.</p>
              ) : (
                <ol className="flex flex-col gap-2.5 max-h-[300px] overflow-y-auto">
                  {data.activity.map((a) => (
                    <li key={a.id} className="text-sm leading-snug">
                      <Link className="font-medium hover:underline" to={a.offerId ? `/offers/${a.offerId}` : "#"}>
                        {a.clientName ?? "Client"}
                      </Link>{" "}
                      <span className="text-slate-500">
                        {ACTIVITY_LABEL[a.type] ?? a.type}
                        {a.type === "reacted" && a.metadata?.reaction ? ` (${String(a.metadata.reaction)})` : ""}
                      </span>
                      <span className="block text-xs text-slate-400">
                        {new Date(a.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>
    </Content>
  );
}
